import React, { useState, useEffect } from 'react';
import { 
  isStandalone, 
  isIOSDevice, 
  isAndroidDevice,
  isPWAInstalled,
  markPWAInstalled,
  clearPWAInstalled
} from '../utils/pwaInstallPrompt';
import '../styles/pwaInstallBanner.css';

/**
 * Smart PWA Install Banner Component
 * Shows appropriate install prompts based on device and installation status
 */
function PWAInstallBanner({ config }) {
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState('ask'); // 'ask', 'remind-installed', 'install-guide'
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [hideOnScroll, setHideOnScroll] = useState(false);
  const [scrollStartY, setScrollStartY] = useState(0);

  useEffect(() => {
    // Check for force-show parameter (for debugging)
    const urlParams = new URLSearchParams(window.location.search);
    const forceShow = urlParams.get('pwa-force') === 'true';
    
    // Check if dismissed in this session (even with force parameter)
    const sessionDismissed = sessionStorage.getItem('pwa-banner-session-dismissed');
    if (sessionDismissed === 'true') {
      console.log('[PWA Banner] Not showing - dismissed in this session');
      return;
    }
    
    if (forceShow) {
      console.log('[PWA Banner] Force show enabled via URL parameter');
      determineBannerType();
      return;
    }
    
    // Don't show if already in standalone mode
    if (isStandalone()) {
      console.log('[PWA Banner] Not showing - already in standalone mode');
      return;
    }

    // Check dismissal history
    const dismissedTime = localStorage.getItem('pwa-banner-dismissed');
    const now = Date.now();
    if (dismissedTime && (now - parseInt(dismissedTime)) < 604800000) { // 7 days
      const daysAgo = Math.floor((now - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24));
      console.log(`[PWA Banner] Not showing - dismissed ${daysAgo} days ago`);
      return;
    }

    // Check engagement metrics
    const visitCount = parseInt(localStorage.getItem('visit-count') || '0');
    const timeOnSite = parseInt(sessionStorage.getItem('time-on-site') || '0');
    
    console.log(`[PWA Banner] Engagement check - Visits: ${visitCount}, Time: ${timeOnSite}ms`);
    
    // Show after 2nd visit or 30 seconds on site
    if (visitCount >= 2 || timeOnSite > 30000) {
      console.log('[PWA Banner] Engagement threshold met, showing banner');
      determineBannerType();
    } else {
      console.log('[PWA Banner] Not enough engagement yet');
    }

    // Track time on site
    const startTime = Date.now();
    const trackTime = setInterval(() => {
      const elapsed = Date.now() - startTime;
      sessionStorage.setItem('time-on-site', elapsed.toString());
      
      // Check if we should show banner
      if (elapsed > 30000 && !showBanner) {
        determineBannerType();
      }
    }, 5000);

    // Handle Android install prompt — store at window level so it survives
    // component re-renders and is accessible from Chrome's native install UI
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.__pwaInstallPrompt = e; // Persist globally
      setDeferredPrompt(e);
      if (!showBanner) {
        determineBannerType();
      }
    };

    // Handle scroll to hide banner
    const handleScroll = () => {
      if (!showBanner || hideOnScroll) return;
      
      const currentY = window.scrollY;
      if (currentY > scrollStartY + 50) {
        // User scrolled down significantly, hide banner
        setHideOnScroll(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearInterval(trackTime);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showBanner, hideOnScroll, scrollStartY]);

  // Track when banner is shown to capture scroll position
  useEffect(() => {
    if (showBanner && !hideOnScroll) {
      setScrollStartY(window.scrollY);
    }
  }, [showBanner]);

  const determineBannerType = () => {
    const pwaInstalled = isPWAInstalled();
    const inStandalone = isStandalone();

    console.log('[PWA Banner] Determining banner type...', {
      isIOS: isIOSDevice(),
      isAndroid: isAndroidDevice(),
      pwaInstalled,
      inStandalone,
      hasDeferredPrompt: !!deferredPrompt
    });

    // If flag says installed but user is NOT in standalone mode,
    // they likely uninstalled — clear stale flag and offer install again
    if (pwaInstalled && !inStandalone) {
      console.log('[PWA Banner] pwa_installed flag set but not in standalone — user likely uninstalled, clearing flag');
      clearPWAInstalled();
      setBannerType('ask');
      setShowBanner(true);
    } else if (pwaInstalled) {
      setBannerType('remind-installed');
      setShowBanner(true);
    } else {
      setBannerType('ask');
      setShowBanner(true);
    }
  };

  const handleInstallClick = async () => {
    // Try React state first, then global fallback
    const prompt = deferredPrompt || window.__pwaInstallPrompt;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          markPWAInstalled();
          handleDismiss();
        }
      } catch (e) {
        console.error('Install prompt failed:', e);
        // Prompt was already used or expired — show manual guide
        setShowIOSGuide(true);
      }
      setDeferredPrompt(null);
      window.__pwaInstallPrompt = null;
    } else {
      // No native prompt available — show manual installation guide
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    sessionStorage.setItem('pwa-banner-session-dismissed', 'true');
  };
  
  // Handle user indicating they have the app
  const handleHasApp = () => {
    markPWAInstalled();
    setBannerType('remind-installed');
  };
  
  // Handle user correcting that they don't have the app
  const handleNoApp = () => {
    clearPWAInstalled();
    setBannerType('ask');
  };

  const renderInstallGuide = () => {
    const android = isAndroidDevice();
    return (
      <div className="ios-install-guide">
        <h3>Install {config?.name || 'this app'}</h3>
        {android ? (
          <ol>
            <li>
              Tap the <strong>three-dot menu</strong>
              <span aria-label="menu icon" style={{fontSize: '16px'}}> ⋮ </span>
              in the top-right corner
            </li>
            <li>
              Tap <span className="menu-item">Add to Home screen</span>
            </li>
            <li>
              Tap <strong>Add</strong> to install
            </li>
          </ol>
        ) : (
          <ol>
            <li>
              Tap the Share button
              <span className="share-icon" aria-label="share icon">↗</span>
              <strong>next to the address bar</strong>
            </li>
            <li>
              Scroll down and tap
              <span className="menu-item">Add to Home Screen</span>
              <span className="plus-icon" aria-label="plus icon">⊕</span>
            </li>
            <li>
              Tap <strong>Add</strong> to install
            </li>
          </ol>
        )}
        <button onClick={() => setShowIOSGuide(false)} className="close-guide">
          Got it!
        </button>
      </div>
    );
  };

  const renderBannerContent = () => {
    console.log('[PWA Banner] Rendering banner with type:', bannerType);
    switch (bannerType) {
      case 'ask':
        return (
          <>
            <div className="banner-icon">🏠</div>
            <div className="banner-text">
              <strong id="pwa-banner-title">Add {config?.short_name || config?.name || 'App'} to your Home Screen</strong>
              <p id="pwa-banner-desc">Enjoy faster access and better performance</p>
            </div>
            <div className="banner-actions">
              <button onClick={handleInstallClick} className="banner-action banner-action-primary">
                Add Now
              </button>
              <button onClick={handleHasApp} className="banner-action banner-action-secondary">
                I Already Have It
              </button>
            </div>
          </>
        );
      
      case 'remind-installed':
        return (
          <>
            <div className="banner-icon">🎉</div>
            <div className="banner-text">
              <strong id="pwa-banner-title">All set!</strong>
              <p id="pwa-banner-desc">Use the {config?.short_name || config?.name || 'App'} icon on your home screen for faster access</p>
            </div>
            <div className="banner-actions">
              <button onClick={handleDismiss} className="banner-action banner-action-primary">
                Got it
              </button>
              <button onClick={handleNoApp} className="banner-action banner-action-secondary">
                Not Installed?
              </button>
            </div>
          </>
        );
      
      case 'install-guide':
        return (
          <>
            <div className="banner-icon">📲</div>
            <div className="banner-text">
              <strong>Easy Installation</strong>
              <p>Tap the Share button <span style={{fontSize: '16px'}}>↗</span> next to the address bar</p>
            </div>
            <button onClick={() => setShowIOSGuide(true)} className="banner-action">
              Show me how
            </button>
          </>
        );
      
      default:
        console.warn('[PWA Banner] Unknown banner type:', bannerType);
        // Fallback to ask state if unknown type
        return (
          <>
            <div className="banner-icon">🏠</div>
            <div className="banner-text">
              <strong id="pwa-banner-title">Add {config?.short_name || config?.name || 'App'} to your Home Screen</strong>
              <p id="pwa-banner-desc">Enjoy faster access and better performance</p>
            </div>
            <div className="banner-actions">
              <button onClick={handleInstallClick} className="banner-action banner-action-primary">
                Add Now
              </button>
              <button onClick={handleHasApp} className="banner-action banner-action-secondary">
                I Already Have It
              </button>
            </div>
          </>
        );
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div 
        className={`pwa-install-banner ${showBanner && !hideOnScroll ? 'show' : ''}`}
        role="dialog"
        aria-labelledby="pwa-banner-title"
        aria-describedby="pwa-banner-desc"
      >
        <button 
          onClick={handleDismiss} 
          className="banner-close"
          aria-label="Close banner"
        >
          ×
        </button>
        {renderBannerContent()}
      </div>
      
      {showIOSGuide && (
        <div className="ios-guide-overlay" onClick={() => setShowIOSGuide(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            {renderInstallGuide()}
          </div>
        </div>
      )}
    </>
  );
}

export default PWAInstallBanner;